/**
 * How many approvals in a row unlock automation: supervisor approvals before a write may run without
 * asking, or drafts sent unchanged before a knowledge answer may auto-send. Every user sees it; an
 * administrator can change it for the account or return it to the deployment's default.
 */

import { useState, type ReactNode } from 'react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePromotionSettings, useUpdatePromotionSettings } from '@/hooks/use-promotion';
import { getApiErrorMessage } from '@/lib/utils';

type ThresholdKind = 'writes' | 'knowledge';

const COPY: Record<ThresholdKind, { loading: string; rule: (value: number) => ReactNode; saved: (value: number) => string }> = {
	writes: {
		loading: 'A write needs supervisor approvals in a row before it can run without asking.',
		rule: (value) => (
			<>
				A write can run without asking only after <strong>{value}</strong> supervisor approvals in a row in this protocol. One reject
				starts the count again.
			</>
		),
		saved: (value) => `A write now needs ${value} approvals in a row`
	},
	knowledge: {
		loading: 'An answer needs drafts sent unchanged in a row before it can auto-send.',
		rule: (value) => (
			<>
				An answer can auto-send only after agents send <strong>{value}</strong> of its drafts unchanged in a row. An edited or replaced
				draft starts the count again.
			</>
		),
		saved: (value) => `An answer now needs ${value} drafts sent unchanged in a row`
	}
};

export const ApprovalThreshold = ({ kind = 'writes' }: { kind?: ThresholdKind }) => {
	const { data: settings } = usePromotionSettings();
	const update = useUpdatePromotionSettings();
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState('');
	const copy = COPY[kind];

	if (!settings) return <span>{copy.loading}</span>;
	const threshold = kind === 'knowledge' ? settings.knowledgeMinConsecutiveApprovals : settings.minConsecutiveApprovals;
	if (!threshold) return <span>{copy.loading}</span>;

	const save = async (value: number | null) => {
		try {
			await update.mutateAsync(kind === 'knowledge' ? { knowledgeMinConsecutiveApprovals: value } : { minConsecutiveApprovals: value });
			toast.success(value === null ? 'Back to the default' : copy.saved(value));
			setEditing(false);
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not change the setting'));
		}
	};

	const parsed = Number(draft);
	const valid = Number.isInteger(parsed) && parsed >= threshold.min && parsed <= threshold.max;

	return (
		<div className='flex flex-1 flex-col gap-1.5' data-testid={kind === 'knowledge' ? 'knowledge-threshold' : 'approval-threshold'}>
			<span>
				{copy.rule(threshold.value)}
				{threshold.source === 'default' ? ' (Default.)' : ` (Set for this account; the default is ${threshold.default}.)`}
			</span>
			{settings.canEdit &&
				(editing ? (
					<span className='flex items-center gap-1.5'>
						<Input
							aria-label='Approvals in a row'
							type='number'
							min={threshold.min}
							max={threshold.max}
							value={draft}
							onChange={(event) => setDraft(event.target.value)}
							className='h-7 w-20 text-xs'
						/>
						<Button size='sm' className='h-7' disabled={!valid || update.isPending} onClick={() => save(parsed)}>
							Save
						</Button>
						{threshold.source === 'account' && (
							<Button size='sm' variant='outline' className='h-7' disabled={update.isPending} onClick={() => save(null)}>
								Use default
							</Button>
						)}
						<Button size='sm' variant='ghost' className='h-7' onClick={() => setEditing(false)}>
							Cancel
						</Button>
					</span>
				) : (
					<Button
						size='sm'
						variant='link'
						className='h-auto self-start p-0 text-xs'
						onClick={() => {
							setDraft(String(threshold.value));
							setEditing(true);
						}}
					>
						Change
					</Button>
				))}
		</div>
	);
};
